import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '../components/shared/Navbar';
import SEO from '../components/SEO';
import API from '../utils/api';
import { trackBlogSearch, trackBlogTagFilter } from '../utils/analytics';

const BlogPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts]   = useState([]);
  const [tags, setTags]     = useState([]);
  const [total, setTotal]   = useState(0);
  const [pages, setPages]   = useState(1);
  const [loading, setLoading] = useState(true);

  const page = parseInt(searchParams.get('page') || '1');
  const tag  = searchParams.get('tag') || '';
  const q    = searchParams.get('q') || '';
  const [search, setSearch] = useState(q);

  useEffect(() => {
    // page/tag/q live in the query string, and the app's route key is the
    // pathname only — so changing a filter here does NOT remount the page and an
    // earlier response can land after a later one, repainting the list with the
    // wrong results while the controls show the newer filter.
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 9 });
    if (tag) params.set('tag', tag);
    if (q)   params.set('q', q);
    Promise.all([
      API.get(`/blog?${params}`),
      API.get('/blog/tags'),
    ]).then(([postsRes, tagsRes]) => {
      if (cancelled) return;
      setPosts(postsRes.data.posts);
      setTotal(postsRes.data.total);
      setPages(postsRes.data.pages);
      setTags(tagsRes.data.tags);
    }).catch(() => {}).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [page, tag, q]);

  const handleSearch = (e) => {
    e.preventDefault();
    const p = new URLSearchParams();
    if (search.trim()) { p.set('q', search.trim()); trackBlogSearch(search.trim()); }
    if (tag) p.set('tag', tag);
    setSearchParams(p);
  };

  const setTag = (t) => {
    const p = new URLSearchParams();
    if (t) { p.set('tag', t); trackBlogTagFilter(t); }
    setSearchParams(p);
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--page-bg)' }}>
      <SEO
        title="Blog — Bedtime Tips, Parenting Guides & Story Ideas"
        description="Expert tips on bedtime routines, children's sleep, parenting advice, and how personalised stories can help your child thrive."
        canonical="/blog"
        noIndex={false}
      />
      <Navbar />

      {/* Hero */}
      <section className="bg-dream text-white py-5">
        <div className="container text-center">
          <span className="badge bg-warning text-dark mb-3 px-3 py-2 rounded-pill">✍️ Dreamy Bedtime Blog</span>
          <h1 className="fw-bold mb-3" style={{ fontFamily: "'Fredoka One',cursive", fontSize: 'clamp(28px,4vw,48px)' }}>
            Stories, Tips & Parenting Guides
          </h1>
          <p style={{ color: '#c9a8ff', fontSize: '18px', maxWidth: 560, margin: '0 auto 24px' }}>
            Expert advice on bedtime routines, children's development, and making the most of personalised stories.
          </p>
          <form onSubmit={handleSearch} className="d-flex justify-content-center gap-2" style={{ maxWidth: 420, margin: '0 auto' }}>
            <input
              className="form-control rounded-pill"
              placeholder="Search articles…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <button type="submit" className="btn btn-warning rounded-pill px-4 fw-bold">Search</button>
          </form>
        </div>
      </section>

      <div className="container py-5">
        <div className="row g-4">

          {/* Main content */}
          <div className="col-lg-9">
            {/* Active filters */}
            {(tag || q) && (
              <div className="mb-3 d-flex align-items-center gap-2 flex-wrap">
                {tag && <span className="badge rounded-pill bg-purple text-white px-3 py-2" style={{ background: '#6f42c1' }}>Tag: {tag}</span>}
                {q  && <span className="badge rounded-pill bg-secondary text-white px-3 py-2">Search: "{q}"</span>}
                <button className="btn btn-sm btn-outline-secondary rounded-pill" onClick={() => { setSearch(''); setSearchParams({}); }}>
                  Clear filters
                </button>
              </div>
            )}

            {loading ? (
              <div className="text-center py-5"><div className="spinner-border" style={{ color: '#6f42c1' }}></div></div>
            ) : posts.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="bi bi-journal-x fs-1 d-block mb-2"></i>
                No articles found.
              </div>
            ) : (
              <div className="row g-4">
                {posts.map(post => (
                  <div key={post._id} className="col-md-6 col-xl-4">
                    <Link to={`/blog/${post.slug}`} style={{ textDecoration: 'none' }}>
                      <div className="card border-0 shadow-sm h-100 card-hover" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                        {post.coverImage ? (
                          <img src={post.coverImage} alt={post.title} style={{ height: 180, objectFit: 'cover', width: '100%' }} />
                        ) : (
                          <div style={{ height: 100, background: 'linear-gradient(135deg,#6f42c1,#9b6fe8)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 36 }}>
                            🌙
                          </div>
                        )}
                        <div className="card-body p-4">
                          <div className="mb-2">
                            {post.tags.slice(0, 2).map(t => (
                              <span key={t} className="badge me-1 rounded-pill" style={{ background: 'var(--purple-section)', color: '#6f42c1', fontSize: '11px' }}>{t}</span>
                            ))}
                          </div>
                          <h5 className="fw-bold mb-2" style={{ color: 'var(--text-main)', lineHeight: 1.3, fontSize: '16px' }}>{post.title}</h5>
                          {post.excerpt && (
                            <p className="text-muted mb-3" style={{ fontSize: '13px', lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                              {post.excerpt}
                            </p>
                          )}
                          <div className="d-flex align-items-center gap-2 mt-auto" style={{ fontSize: '12px', color: '#9ca3af' }}>
                            <i className="bi bi-clock"></i> {post.readingTime} min read
                            <span className="ms-auto">{new Date(post.publishedAt).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {pages > 1 && (
              <div className="d-flex justify-content-center mt-5 gap-2">
                <button
                  className="btn btn-outline-secondary rounded-pill"
                  disabled={page === 1}
                  onClick={() => { const p = new URLSearchParams(searchParams); p.set('page', page - 1); setSearchParams(p); }}
                >
                  <i className="bi bi-chevron-left"></i> Previous
                </button>
                <span className="align-self-center text-muted small">Page {page} of {pages}</span>
                <button
                  className="btn btn-outline-secondary rounded-pill"
                  disabled={page >= pages}
                  onClick={() => { const p = new URLSearchParams(searchParams); p.set('page', page + 1); setSearchParams(p); }}
                >
                  Next <i className="bi bi-chevron-right"></i>
                </button>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="col-lg-3">
            {/* Tags */}
            {tags.length > 0 && (
              <div className="card border-0 shadow-sm p-4 mb-4" style={{ borderRadius: '14px' }}>
                <h6 className="fw-bold mb-3" style={{ color: 'var(--text-main)' }}>Browse by Topic</h6>
                <div className="d-flex flex-wrap gap-2">
                  <button
                    className={`btn btn-sm rounded-pill ${!tag ? 'btn-dream' : 'btn-outline-secondary'}`}
                    onClick={() => setTag('')}
                  >
                    All
                  </button>
                  {tags.map(t => (
                    <button
                      key={t}
                      className={`btn btn-sm rounded-pill ${tag === t ? 'btn-dream' : 'btn-outline-secondary'}`}
                      onClick={() => setTag(t)}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* CTA */}
            <div className="card border-0 p-4 text-center" style={{ borderRadius: '14px', background: 'linear-gradient(135deg,#6f42c1,#9b6fe8)' }}>
              <div style={{ fontSize: 36 }}>🌙</div>
              <h6 className="fw-bold text-white mt-2 mb-2">Try Dreamy Bedtime</h6>
              <p className="text-white mb-3" style={{ fontSize: '13px', opacity: 0.9 }}>
                Generate personalised bedtime stories for your child in seconds.
              </p>
              <Link to="/register" className="btn btn-warning btn-sm rounded-pill fw-bold w-100">
                Start Free
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{ borderTop: '1px solid var(--border-purple)', marginTop: '48px', paddingTop: '28px', paddingBottom: '32px', background: 'var(--card-bg)' }}>
        <div className="container text-center">
          <p className="text-muted small mb-2">
            <Link to="/about" style={{ color: '#6f42c1' }}>About</Link>
            {' · '}
            <Link to="/faq" style={{ color: '#6f42c1' }}>FAQ</Link>
            {' · '}
            <Link to="/guide" style={{ color: '#6f42c1' }}>Help Guide</Link>
            {' · '}
            <Link to="/contact" style={{ color: '#6f42c1' }}>Contact</Link>
            {' · '}
            <Link to="/privacy" style={{ color: '#6f42c1' }}>Privacy</Link>
            {' · '}
            <Link to="/terms" style={{ color: '#6f42c1' }}>Terms</Link>
          </p>
          <p className="text-muted" style={{ fontSize: '12px', margin: 0 }}>© 2026 Prajapati LLC</p>
        </div>
      </div>
    </div>
  );
};

export default BlogPage;
